import { useCallback, useContext, useEffect, useState } from "react";
import { AppContext } from "../AppContextProvider";
import dealsService from "../services/dealsService";

const rowsOf = (resp) => {
  const rows = resp?.data?.data;
  return Array.isArray(rows) ? rows : [];
};

/**
 * The deals list's whole-portfolio data: every deal (up to 5000), every
 * lease/deed, every lease/deed tract and every deal-level payment. Loaded
 * once by the page and handed to both the KPI strip and the map band, so
 * opening the map costs no second copy of the same four lists.
 *
 * Fetched on mount; `reload` re-fires all four (the KPI strip's Refresh).
 * Any failure fails the lot, as the KPI strip always has. Row scoping for
 * external brokers is applied server-side on each call.
 *
 * @returns {{ deals: object[], leaseDeeds: object[], tracts: object[],
 *   payments: object[], loading: boolean, errorMsg: string|null,
 *   reload: () => Promise<void> }}
 */
const useDealsOverview = () => {
  const { token, getToken, isTokenExpired, user } = useContext(AppContext);

  const [deals, setDeals] = useState([]);
  const [leaseDeeds, setLeaseDeeds] = useState([]);
  const [tracts, setTracts] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState(null);

  const reload = useCallback(async () => {
    try {
      setLoading(true);
      setErrorMsg(null);

      const userOid = user?.idTokenClaims?.oid || user?.localAccountId;
      if (!userOid) throw new Error("OID not found in user claims.");

      let tk = token;
      if (!tk || isTokenExpired(tk)) tk = await getToken();

      const [dealsResp, ldResp, trResp, payResp] = await Promise.all([
        dealsService.listDeals({
          token: tk,
          userOid,
          filters: { limit: 5000 },
        }),
        dealsService.listAllLeaseDeeds({ token: tk, userOid }),
        dealsService.listAllLeaseDeedTracts({ token: tk, userOid }),
        dealsService.listAllPayments({
          token: tk,
          userOid,
          parentTable: "deal",
        }),
      ]);

      setDeals(rowsOf(dealsResp));
      setLeaseDeeds(rowsOf(ldResp));
      setTracts(rowsOf(trResp));
      setPayments(rowsOf(payResp));
    } catch (err) {
      console.error("Deals overview load failed:", err);
      setErrorMsg(err?.message || String(err));
    } finally {
      setLoading(false);
    }
  }, [token, getToken, isTokenExpired, user]);

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { deals, leaseDeeds, tracts, payments, loading, errorMsg, reload };
};

export default useDealsOverview;
